import { useEffect, useMemo, useState } from "react";

import {
  CadmusEditorContent,
  useCadmusEditor,
} from "@vericus/cadmus-editor-prosemirror";
import { Button, Checkbox, Modal, Spacer, Text } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { useAppDispatch } from "@/data/hooks";
import {
  acceptSubmissionDeclaration,
  declineSubmissionDeclaration,
} from "@/features/authority";

import {
  clearStaleAcceptance,
  isGenAiAccepted,
  removeGenAiAcceptance,
  setGenAiAccepted,
} from "./gen-ai-acceptance-storage";
import * as styles from "./gen-ai-guidelines-modal-styles.css";

export interface GenAiGuidelinesModalProps {
  content: string;
  acceptanceScopeId: string;
  taskNoticeId: string;
}

export const ConnectedGenAiGuidelinesModal = create<GenAiGuidelinesModalProps>(
  (props) => {
    const modal = useModal();
    const dispatch = useAppDispatch();
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
      if (modal.visible) {
        setAccepted(isGenAiAccepted(props.acceptanceScopeId, props.taskNoticeId));
      }
    }, [modal.visible, props.acceptanceScopeId, props.taskNoticeId]);

    const editorContent = useMemo(
      () => JSON.parse(props.content),
      [props.content]
    );

    const editor = useCadmusEditor(
      {
        editorId: "genai-guidelines-readonly",
        editorA11yLabel: "Generative AI guidelines",
        content: editorContent,
        editable: false,
      },
      [editorContent]
    );

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setAccepted(checked);
      if (checked) {
        setGenAiAccepted(props.acceptanceScopeId, props.taskNoticeId);
        dispatch(
          acceptSubmissionDeclaration({ submissionDeclaration: "generativeAi" })
        );
      } else {
        removeGenAiAcceptance(props.acceptanceScopeId, props.taskNoticeId);
        dispatch(
          declineSubmissionDeclaration({
            submissionDeclaration: "generativeAi",
          })
        );
      }
    };

    const handleConfirm = () => {
      clearStaleAcceptance(props.acceptanceScopeId, props.taskNoticeId);
      setGenAiAccepted(props.acceptanceScopeId, props.taskNoticeId);
      dispatch(
        acceptSubmissionDeclaration({ submissionDeclaration: "generativeAi" })
      );
      modal.remove();
    };

    return (
      <Modal.Root
        open={modal.visible}
        onOpenChange={(open) => {
          if (!open) modal.hide();
        }}
      >
        <Modal.Content
          width={1048}
          onCloseButtonClick={() => modal.hide()}
          className={styles.modalContent}
        >
          <Spacer spacing={16} />
          <Modal.Title asChild>
            <Text kind="headingFour" textAlign="center">
              Additional Generative AI Instructions
            </Text>
          </Modal.Title>
          <Spacer spacing={16} />
          <Modal.Description asChild>
            <div className={styles.editorContainer}>
              {editor && (
                <CadmusEditorContent
                  editor={editor}
                  pageMode={false}
                  fullWidth
                />
              )}
            </div>
          </Modal.Description>
          <Spacer spacing={24} />
          <div className={styles.footer}>
            <Checkbox
              checked={accepted}
              onChange={(e) => handleCheckboxChange(e)}
            >
              <Text kind="bodySm" className={styles.checkboxLabel}>
                I have read and understood the instructions provided on the
                usage of Generative AI
              </Text>
            </Checkbox>
            <Button
              kind="secondary"
              disabled={!accepted}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </div>
        </Modal.Content>
      </Modal.Root>
    );
  }
);
